import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Check, Car, Bike } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = {
  en: { currency: '£', vatLabel: 'VAT' },
  it: { currency: '€', vatLabel: 'IVA' },
  ro: { currency: 'Lei', vatLabel: 'TVA' }
};

const LABELS = {
  en: {
    title: 'SERVICE SHEET',
    authentic: 'AUTHENTIC',
    authenticDesc: 'This document is verified and registered',
    serviceProvider: 'Service Provider',
    client: 'Client',
    vehicle: 'Vehicle Information',
    vin: 'VIN Number',
    registration: 'Registration',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    mileage: 'Mileage',
    services: 'Services Performed',
    description: 'Description',
    qty: 'Qty',
    price: 'Price',
    amount: 'Amount',
    subtotal: 'Subtotal',
    vat: 'VAT',
    total: 'Total',
    date: 'Service Date',
    car: 'Car',
    moto: 'Motorcycle',
    serviced: 'SERVICED',
    check: 'CHECK',
    notes: 'Notes',
    signature: 'Signature & Stamp',
    notFound: 'Document not found',
    notFoundDesc: 'This service sheet could not be verified.'
  },
  it: {
    title: 'SCHEDA SERVIZIO',
    authentic: 'AUTENTICO',
    authenticDesc: 'Documento verificato e registrato',
    serviceProvider: 'Centro Assistenza',
    client: 'Cliente',
    vehicle: 'Informazioni Veicolo',
    vin: 'Numero Telaio',
    registration: 'Targa',
    make: 'Marca',
    model: 'Modello',
    year: 'Anno',
    mileage: 'Chilometraggio',
    services: 'Servizi Eseguiti',
    description: 'Descrizione',
    qty: 'Qtà',
    price: 'Prezzo',
    amount: 'Importo',
    subtotal: 'Subtotale',
    vat: 'IVA',
    total: 'Totale',
    date: 'Data Servizio',
    car: 'Auto',
    moto: 'Moto',
    serviced: 'REVISIONATO',
    check: 'CONTROLLATO',
    notes: 'Note',
    signature: 'Firma e Timbro',
    notFound: 'Documento non trovato',
    notFoundDesc: 'Questa scheda servizio non può essere verificata.'
  },
  ro: {
    title: 'FIȘĂ SERVICE',
    authentic: 'AUTENTIC',
    authenticDesc: 'Document verificat și înregistrat',
    serviceProvider: 'Service Auto',
    client: 'Client',
    vehicle: 'Informații Vehicul',
    vin: 'Serie Șasiu (VIN)',
    registration: 'Nr. Înmatriculare',
    make: 'Marcă',
    model: 'Model',
    year: 'An Fabricație',
    mileage: 'Kilometraj',
    services: 'Lucrări Efectuate',
    description: 'Descriere',
    qty: 'Cant.',
    price: 'Preț',
    amount: 'Valoare',
    subtotal: 'Subtotal',
    vat: 'TVA',
    total: 'Total',
    date: 'Data Service',
    car: 'Autoturism',
    moto: 'Motocicletă',
    serviced: 'VERIFICAT',
    check: 'CONTROL',
    notes: 'Observații',
    signature: 'Semnătură și ștampilă',
    notFound: 'Document negăsit',
    notFoundDesc: 'Această fișă de service nu a putut fi verificată.'
  }
};

const formatCurrency = (amount, lang) => {
  const config = LANGUAGES[lang] || LANGUAGES.en;
  return `${config.currency}${Number(amount || 0).toFixed(2)}`;
};

const ServiceSheetVerifyPage = () => {
  const { sheetId, code } = useParams();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchSheet = async () => {
      try {
        // Use code (short) or sheetId (full UUID) - both work
        const identifier = code || sheetId;
        const response = await axios.get(`${BACKEND_URL}/api/service/verify/${identifier}`);
        setSheet(response.data);
        
        // Hide URL from address bar after loading
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, '', '/v/doc');
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchSheet();
  }, [sheetId, code]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const labels = sheet ? (LABELS[sheet.language] || LABELS.en) : LABELS.en;
  const hidePrices = sheet?.hide_prices || false;

  if (error || !sheet) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center border-t-4 border-red-500">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <span className="text-red-500 text-3xl">✕</span>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">{labels.notFound}</h1>
          <p className="text-gray-600">{labels.notFoundDesc}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-2">
      {/* Authentic Badge - Fixed top */}
      <div className="fixed top-0 left-0 right-0 bg-green-600 text-white py-2 px-4 flex items-center justify-center gap-2 shadow-md z-50">
        <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
          <Check size={16} className="text-green-600" />
        </div>
        <span className="font-bold tracking-wide">{labels.authentic}</span>
        <span className="text-green-200 text-sm hidden sm:inline">• {labels.authenticDesc}</span>
      </div>

      {/* Main Document */}
      <div className="max-w-2xl mx-auto mt-14 bg-white shadow-xl rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-800 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-xl font-bold">{labels.title}</h1>
              <p className="text-gray-400 text-sm">#{sheet.sheet_number}</p>
              <p className="text-gray-400 text-sm">{labels.date}: {sheet.service_date}</p>
            </div>
            <div className={`px-4 py-2 rounded border-2 font-bold text-sm transform rotate-[-3deg] ${
              sheet.stamp_type === 'serviced' 
                ? 'border-green-400 text-green-400' 
                : 'border-blue-400 text-blue-400'
            }`}>
              {sheet.stamp_type === 'serviced' ? labels.serviced : labels.check}
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Provider & Client */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="border border-gray-200 rounded p-3 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">{labels.serviceProvider}</h3>
              <p className="font-semibold text-gray-800">{sheet.provider_name}</p>
              <p className="text-sm text-gray-600">{sheet.provider_main_address}</p>
              {sheet.provider_sub_unit && <p className="text-sm text-gray-600">{sheet.provider_sub_unit}</p>}
              <p className="text-sm text-gray-600">{sheet.provider_phone}</p>
            </div>
            <div className="border border-gray-200 rounded p-3 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">{labels.client}</h3>
              <p className="font-semibold text-gray-800">{sheet.client_name}</p>
              {sheet.client_phone && <p className="text-sm text-gray-600">{sheet.client_phone}</p>}
              {sheet.client_address && <p className="text-sm text-gray-600">{sheet.client_address}</p>}
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="border border-gray-200 rounded p-3 bg-gray-50">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
              {sheet.vehicle_type === 'car' ? <Car size={14} /> : <Bike size={14} />}
              {labels.vehicle} ({sheet.vehicle_type === 'car' ? labels.car : labels.moto})
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <div><span className="text-gray-500">{labels.vin}:</span> <strong className="font-mono text-gray-800">{sheet.vin_number}</strong></div>
              <div><span className="text-gray-500">{labels.registration}:</span> <strong className="font-mono text-gray-800">{sheet.registration_number}</strong></div>
              <div><span className="text-gray-500">{labels.mileage}:</span> <strong className="text-gray-800">{sheet.mileage}</strong></div>
              <div><span className="text-gray-500">{labels.make}:</span> <span className="text-gray-800">{sheet.vehicle_make}</span></div>
              <div><span className="text-gray-500">{labels.model}:</span> <span className="text-gray-800">{sheet.vehicle_model}</span></div>
              <div><span className="text-gray-500">{labels.year}:</span> <span className="text-gray-800">{sheet.vehicle_year}</span></div>
            </div>
          </div>

          {/* Services Table */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{labels.services}</h3>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-2 text-left">{labels.description}</th>
                  <th className="border border-gray-300 p-2 text-center w-14">{labels.qty}</th>
                  {!hidePrices && (
                    <>
                      <th className="border border-gray-300 p-2 text-right w-20">{labels.price}</th>
                      <th className="border border-gray-300 p-2 text-right w-20">{labels.amount}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {sheet.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td className="border border-gray-300 p-2">{item.description}</td>
                    <td className="border border-gray-300 p-2 text-center">{item.quantity}</td>
                    {!hidePrices && (
                      <>
                        <td className="border border-gray-300 p-2 text-right">{formatCurrency(item.unit_price, sheet.language)}</td>
                        <td className="border border-gray-300 p-2 text-right">{formatCurrency(item.amount, sheet.language)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bottom: Totals only (no stamp on verification page) */}
          <div className="flex justify-end items-end pt-2">
            {/* Totals */}
            {!hidePrices && (
              <div className="w-36 text-sm">
                <div className="flex justify-between py-0.5 border-b border-gray-200">
                  <span className="text-gray-600 text-xs">{labels.subtotal}:</span>
                  <strong className="text-xs text-gray-800">{formatCurrency(sheet.subtotal, sheet.language)}</strong>
                </div>
                {sheet.include_vat && (
                  <div className="flex justify-between py-0.5 border-b border-gray-200">
                    <span className="text-gray-600 text-xs">{labels.vat} ({sheet.vat_rate}%):</span>
                    <strong className="text-xs text-gray-800">{formatCurrency(sheet.vat_amount, sheet.language)}</strong>
                  </div>
                )}
                <div className="flex justify-between py-1 font-bold">
                  <span className="text-gray-800">{labels.total}:</span>
                  <span className="text-green-600">{formatCurrency(sheet.total, sheet.language)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          {sheet.notes && (
            <div className="border-t pt-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">{labels.notes}</h3>
              <p className="text-sm text-gray-700">{sheet.notes}</p>
            </div>
          )}
        </div>

        {/* Green Verified Footer */}
        <div className="bg-green-50 border-t-2 border-green-500 p-3 flex items-center justify-center gap-2">
          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
            <Check size={12} className="text-white" />
          </div>
          <span className="text-green-700 font-semibold text-sm">{labels.authentic}</span>
          <span className="text-green-600 text-xs">• {labels.authenticDesc}</span>
        </div>
      </div>
    </div>
  );
};

export default ServiceSheetVerifyPage;
